import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { WorldRenderer } from "../WorldRenderer";
import { useEffect, useState } from "react";
import { PointSelectedEvent } from "./PlanetService";
import { ExpandMore, PushPin, PushPinOutlined } from "@mui/icons-material";
import { PointProps } from "./propPanels/PointProps";
import { SiteAnchorVisual } from "../../visuals/SiteAnchorVisual";
import { SiteList } from "./propPanels/SiteList";
import { BaseVisual } from "../../visuals/BaseVisual";
import { SiteProps } from "./propPanels/SiteProps";
import { WREvents } from "../WREvents";
import { VisualProps } from "./propPanels/VisualProps";
import { VisualList } from "./propPanels/VisualList";
import { ScenarioList } from "./propPanels/ScenarioList";
import { AgentStatesList } from "./propPanels/AgentStatesList";

export function WorldPropertiesPanel({ wr }: { wr: WorldRenderer }) {
  const [selectedPoint, setSelectedPoint] = useState<PointSelectedEvent | null>(
    null
  );
  const [siteAnchors, setSiteAnchors] = useState<SiteAnchorVisual[]>([]);
  const [selectedVisual, setSelectedVisual] = useState<BaseVisual | null>(null);
  const [selectedSite, setSelectedSite] = useState<SiteAnchorVisual | null>(
    null
  );
  const [activeSite, setActiveSite] = useState<SiteAnchorVisual | null>(null);
  const [expanded, setExpanded] = useState<string | false>("sites");
  const [pinned, setPinned] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!wr) return;

    const selectedPointListener = (point: PointSelectedEvent) => {
      if (selectedSite && !selectedSite.active) {
        setSelectedSite(null);
      }
      setSelectedPoint(point);
    };
    wr.onEvent(WREvents.PointSelected, selectedPointListener);

    const sitesUpdatedListener = (anchors: Map<string, SiteAnchorVisual>) => {
      setSiteAnchors(Array.from(anchors.values()));
    };
    wr.onEvent(WREvents.SitesUpdated, sitesUpdatedListener);

    const visualSelectedListener = (visual: BaseVisual) => {
      setSelectedPoint(null);
      if (visual instanceof SiteAnchorVisual) {
        visual.highlightSelectVisual(null);
        setSelectedSite(visual);
        setSelectedVisual(null);
      } else {
        setSelectedVisual(visual);
        if (
          visual &&
          visual.siteAnchorId &&
          wr.dataService.activeSite?._id === visual.siteAnchorId
        ) {
          const activeSite = wr.dataService.getActiveSite();
          activeSite.highlightSelectVisual(visual);
        }
      }
    };
    wr.onEvent(WREvents.VisualSelected, visualSelectedListener);

    const visualRemovedListener = (visual: BaseVisual) => {
      if (selectedVisual && selectedVisual._id === visual._id) {
        setSelectedVisual(null);
      }
      if (selectedSite && selectedSite._id === visual.siteAnchorId) {
        setSelectedSite(null);
      }
    };
    wr.onEvent(WREvents.VisualRemoved, visualRemovedListener);

    const siteActivatedListener = (site: SiteAnchorVisual) => {
      setActiveSite(site);
    };
    wr.onEvent(WREvents.SiteActivated, siteActivatedListener);

    const siteDeactivatedListener = () => {
      setActiveSite(null);
    };
    wr.onEvent(WREvents.SiteDeactivated, siteDeactivatedListener);

    const onVisualDeleted = (visual: BaseVisual) => {
      if (selectedVisual && selectedVisual._id === visual._id) {
        setSelectedVisual(null);
      }
    };
    wr.onEvent(WREvents.VisualRemoved, onVisualDeleted);

    return () => {
      wr.off(WREvents.PointSelected, selectedPointListener);
      wr.off(WREvents.SitesUpdated, sitesUpdatedListener);
      wr.off(WREvents.VisualSelected, visualSelectedListener);
      wr.off(WREvents.VisualRemoved, visualRemovedListener);
      wr.off(WREvents.SiteActivated, siteActivatedListener);
      wr.off(WREvents.SiteDeactivated, siteDeactivatedListener);
      wr.off(WREvents.VisualRemoved, onVisualDeleted);
    };
  }, [wr]);

  const toggleExpand = (panel: string) => {
    if (expanded === panel) {
      setExpanded(false); // collapse if already expanded
    } else {
      setExpanded(panel); // expand this one
    }
  };

  const togglePin = (panel: string) => {
    setPinned((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(panel)) {
        newSet.delete(panel);
      } else {
        newSet.add(panel);
      }
      return newSet;
    });
  };

  const isOpen = (panel: string) => expanded === panel || pinned.has(panel);

  const renderAccordionSummary = (panel: string, label: string) => (
    <AccordionSummary
      expandIcon={<ExpandMore />}
      onClick={() => toggleExpand(panel)}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {label}
      </Typography>
      {/* <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          togglePin(panel);
        }}
      >
        {pinned.has(panel) ? (
          <PushPin fontSize="inherit" />
        ) : (
          <PushPinOutlined fontSize="inherit" />
        )}
      </IconButton> */}
    </AccordionSummary>
  );

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Accordion disableGutters expanded={isOpen("sites")}>
        {renderAccordionSummary("sites", `Sites (${siteAnchors.length})`)}
        <AccordionDetails>
          <SiteList
            wr={wr}
            sites={siteAnchors}
            pinned={pinned}
            panel="sites"
            togglePin={togglePin}
          />
        </AccordionDetails>
      </Accordion>

      {selectedPoint && (
        <Accordion disableGutters expanded={isOpen("location")}>
          {renderAccordionSummary("location", "Location")}
          <AccordionDetails>
            <PointProps
              wr={wr}
              point={selectedPoint}
              pinned={pinned}
              panel="location"
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {selectedSite && (
        <Accordion disableGutters expanded={isOpen("site")}>
          {renderAccordionSummary("site", `Site: ${selectedSite.id}`)}
          <AccordionDetails>
            <SiteProps
              wr={wr}
              site={selectedSite}
              pinned={pinned}
              panel={"site"}
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {selectedVisual && (
        <Accordion disableGutters expanded={isOpen("object")}>
          {renderAccordionSummary("object", `Object ${selectedVisual.id}`)}
          <AccordionDetails>
            <VisualProps
              wr={wr}
              visual={selectedVisual}
              pinned={pinned}
              panel={"object"}
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {selectedVisual && (selectedVisual as any).agent && (
        <Accordion disableGutters expanded={isOpen("agent")}>
          {renderAccordionSummary("agent", "Agent")}
          <AccordionDetails>
            <AgentStatesList
              agent={(selectedVisual as any).agent}
              pinned={pinned}
              panel={"agent"}
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {activeSite && (
        <Accordion disableGutters expanded={isOpen("objects")}>
          {renderAccordionSummary("objects", "Objects")}
          <AccordionDetails>
            <VisualList
              wr={wr}
              pinned={pinned}
              panel={"objects"}
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {activeSite && (
        <Accordion disableGutters expanded={isOpen("scenarios")}>
          {renderAccordionSummary("scenarios", "Scenarios")}
          <AccordionDetails>
            <ScenarioList
              wr={wr}
              pinned={pinned}
              panel={"scenarios"}
              togglePin={togglePin}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
